"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Session } from "next-auth";
import {
  MessageCircle,
  UserCircle,
  Menu,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  LogIn,
  Headphones,
  ShieldAlert,
  RefreshCw,
  Bell,
  CalendarClock,
  Gift,
} from "lucide-react";
import DefaultImg from "@/app/_lib/assets/images/default-profile-img.png";
import { Sheet, SheetContent, SheetTrigger } from "@/app/_components/ui/sheet";
import { resendVerificationEmail } from "@/app/_lib/actions/create-guest-conversation";

const navItems = [
  {
    label: "Messages",
    href: "/dashboard/messages",
    icon: MessageCircle,
    description: "Your conversations",
  },
  {
    label: "Support",
    href: "/dashboard/support-chat",
    icon: Headphones,
    description: "Get help",
  },
  {
    label: "Bookings",
    href: "/dashboard/bookings",
    icon: CalendarClock,
    description: "Your call bookings",
  },
  {
    label: "Promotions",
    href: "/dashboard/promotions",
    icon: Gift,
    description: "Current offers and deals",
  },
  {
    label: "Notifications",
    href: "/dashboard/notifications",
    icon: Bell,
    description: "Updates and alerts",
  },
  {
    label: "My Profile",
    href: "/dashboard/my-profile",
    icon: UserCircle,
    description: "Edit your profile",
  },
];

interface ProfileData {
  hasPassword: boolean;
  hasPhone: boolean;
  hasAvatar: boolean;
  hasName: boolean;
  isOAuth: boolean;
}

function profileCompletion(data: ProfileData): number {
  let score = 0;
  if (data.hasName) score += 20;
  if (data.hasPassword) score += 50;
  if (data.hasAvatar) score += 20;
  if (data.hasPhone) score += 10;
  return score;
}

function ProfileCard({
  userEmail,
  collapsed,
}: {
  profileData: ProfileData;
  userEmail: string;
  collapsed: boolean;
}) {
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setResending(true);
    await resendVerificationEmail(userEmail);
    setResending(false);
    setResendCooldown(60);
  };

  if (collapsed) {
    return (
      <div className="px-2 pt-1 pb-2">
        <button
          onClick={handleResend}
          title={resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Verify your email"}
          disabled={resending || resendCooldown > 0}
          className="flex items-center justify-center w-full py-2.5 rounded-[10px] bg-primary/5 border border-primary/20 hover:bg-primary/10 transition-colors disabled:opacity-60"
        >
          {resendCooldown > 0 ? (
            <ShieldAlert size={15} className="text-primary" />
          ) : (
            <RefreshCw
              size={14}
              className={`text-primary ${resending ? "animate-spin" : ""}`}
            />
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="mx-2 mt-1 mb-2 rounded-[13px] border border-priamry/20 bg-primary/5 p-3 flex flex-col gap-2">
      <div className="flex items-start gap-2.5">
        <div className="w-7 h-7 rounded-full bg-primary/10 border border-border flex items-center justify-center flex-shrink-0 mt-0.5">
          <ShieldAlert size={13} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold text-primary leading-tight">
            Verify your email
          </p>
          <p className="text-[10px] text-primary/80 mt-0.5 leading-snug">
            Click the link we sent to keep access to your account.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <button
          onClick={() => window.open(`mailto:${userEmail}`, "_blank")}
          className="w-full py-1.5 text-center text-[11px] font-semibold bg-primary text-white rounded-lg hover:opacity-90 transition-opacity"
        >
          Open my inbox
        </button>
        <button
          onClick={handleResend}
          disabled={resending || resendCooldown > 0}
          className="w-full py-1.5 text-center text-[11px] font-medium text-primary/70 hover:bg-primary/10 rounded-lg transition-colors flex items-center justify-center gap-1 disabled:opacity-60"
        >
          <RefreshCw size={10} className={resending ? "animate-spin" : ""} />
          {resendCooldown > 0 ? `Sent! Resend in ${resendCooldown}s` : "Resend email"}
        </button>
      </div>
    </div>
  );
}

function SidebarContent({
  session,
  collapsed,
  onCollapse,
  onNavigate,
  profileData,
}: {
  session: Session | null;
  collapsed?: boolean;
  onCollapse?: () => void;
  onNavigate?: () => void;
  profileData?: ProfileData | null;
}) {
  const pathname = usePathname();
  const user = session?.user ?? null;
  const name = user
    ? `${user.firstname || ""} ${user.lastname || ""}`.trim()
    : null;
  const imageUrl = user?.image
    ? user.image.startsWith("https")
      ? user.image
      : `https://res.cloudinary.com/${process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME}/image/upload/${user.image}`
    : null;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Profile / Guest header — collapse button lives here when expanded */}
      <div
        className={`border-b border-border flex-shrink-0 flex items-center ${
          collapsed ? "h-[72px] justify-center" : "h-[80px] px-4"
        }`}
      >
        {user ? (
          collapsed ? (
            <div className="relative">
              <Image
                src={imageUrl || DefaultImg}
                width={36}
                height={36}
                alt={name || "User"}
                className="rounded-full w-9 h-9 object-cover border-2 border-border"
              />
              <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-primary rounded-full border-2 border-background" />
            </div>
          ) : (
            <div className="flex items-center gap-3 w-full min-w-0">
              <div className="relative w-10 h-10 flex-shrink-0">
                <Image
                  src={imageUrl || DefaultImg}
                  width={40}
                  height={40}
                  alt={name || "User"}
                  className="rounded-full w-10 h-10 object-cover border-2 border-border"
                />
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-primary rounded-full border-2 border-background" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm truncate">{name || "User"}</p>
                <p className="text-muted text-xs truncate">{user.email}</p>
              </div>
              {onCollapse && (
                <button
                  onClick={onCollapse}
                  title="Collapse sidebar"
                  className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-[8px] text-muted hover:text-foreground hover:bg-accent transition-colors"
                >
                  <PanelLeftClose size={14} />
                </button>
              )}
            </div>
          )
        ) : collapsed ? (
          <Link href="/login" title="Sign in">
            <div className="w-9 h-9 rounded-full bg-muted/5 flex items-center justify-center">
              <LogIn size={15} className="text-muted-foreground" />
            </div>
          </Link>
        ) : (
          <div className="flex items-center w-full gap-2">
            <div className="flex flex-col gap-1 flex-1 min-w-0">
              <p className="text-xs text-muted-foreground">Not signed in</p>
              <Link
                href="/login"
                className="text-xs font-medium text-primary hover:underline"
              >
                Sign in →
              </Link>
            </div>
            {onCollapse && (
              <button
                onClick={onCollapse}
                title="Collapse sidebar"
                className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-[8px] text-muted hover:text-foreground hover:bg-accent transition-colors"
              >
                <PanelLeftClose size={14} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="p-2 flex flex-col gap-0.5">
        {collapsed && onCollapse && (
          <button
            onClick={onCollapse}
            title="Expand sidebar"
            className="flex items-center justify-center w-full py-2.5 rounded-[10px] text-muted hover:text-foreground hover:bg-accent transition-colors"
          >
            <PanelLeftOpen size={18} />
          </button>
        )}

        {navItems.map((item) => {
          const Icon = item.icon;
          const active =
            pathname === item.href || pathname.startsWith(item.href + "/");
          return (
            <Link
              key={item.href}
              href={item.href}
              onClick={onNavigate}
              title={collapsed ? item.label : undefined}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-[10px] transition-all duration-200 group ${
                collapsed ? "justify-center px-0" : ""
              } ${
                active
                  ? "bg-primary/8 text-primary"
                  : "text-muted hover:text-foreground hover:bg-accent"
              }`}
            >
              <Icon
                size={18}
                className={
                  active
                    ? "text-primary"
                    : "text-muted group-hover:text-foreground"
                }
              />
              {!collapsed && (
                <>
                  <span className="text-sm flex-1">{item.label}</span>
                  {active && (
                    <ChevronRight size={14} className="text-primary" />
                  )}
                </>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Email verification card — right below nav links. OAuth users
          (Google/etc.) are already verified, so never nag them. */}
      {user && profileData && !profileData.hasPassword && !profileData.isOAuth && (
        <ProfileCard
          profileData={profileData}
          userEmail={user.email ?? ""}
          collapsed={!!collapsed}
        />
      )}

      <div className="flex-1" />
    </div>
  );
}

export default function DashboardSidebar({
  session,
  profileData,
}: {
  session: Session | null;
  profileData?: ProfileData | null;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();

  // Load persisted collapse state
  useEffect(() => {
    try {
      const saved = localStorage.getItem("dashSidebarCollapsed");
      if (saved === "true") setCollapsed(true);
    } catch {}
  }, []);

  // Auto-collapse when on a chat detail or new conversation page
  const isChatDetail = /\/(messages|support-chat)\/[^/]+$/.test(pathname);

  useEffect(() => {
    if (isChatDetail) {
      setCollapsed(true);
    } else {
      // Restore user's saved preference when leaving chat detail
      try {
        const saved = localStorage.getItem("dashSidebarCollapsed");
        setCollapsed(saved === "true");
      } catch {
        setCollapsed(false);
      }
    }
  }, [isChatDetail]);

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem("dashSidebarCollapsed", String(next));
    } catch {}
  };

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`hidden md:flex flex-col border-r border-border flex-shrink-0 sticky max-h-[calc(100vh-60px)] top-[60px] h-full transition-[width] duration-300 overflow-hidden relative ${
          collapsed ? "w-[60px]" : "w-[240px]"
        }`}
      >
        <SidebarContent
          session={session}
          collapsed={collapsed}
          onCollapse={toggleCollapse}
          profileData={profileData}
        />
        {/* Right-edge collapse trigger */}
        <div
          className="absolute top-0 right-0 w-1.5 h-full cursor-col-resize z-20 hover:bg-primary/20 transition-colors"
          onClick={toggleCollapse}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        />
      </aside>

      {/* Mobile hamburger */}
      <div className="md:hidden fixed top-[8px] right-1 z-50">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button className="w-12 h-12 bg-white text-primary rounded-full flex items-center justify-center">
              <Menu size={27} />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[260px] p-0">
            <SidebarContent
              session={session}
              onNavigate={() => setMobileOpen(false)}
              profileData={profileData}
            />
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
}
